import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useNavigate } from "react-router";
import OtpInput from "react18-input-otp";
import {
  useLazyProfileQuery,
  useLazyValidateEmailOTPQuery,
} from "../../../../../redux/api/user/userAPI";
import { OvstErrorCode } from "../../../enums/ovst-error-codes";
import { AppRoute } from "../../../interfaces/app-routes";
import OtpVerificationFooter from "../../../shared-components/otp-verification-footer/otp-verification-footer";
import AppButton from "../../../ui-elements/buttons/app-button/app-button";
import SpinnerModal from "../../../ui-elements/spinner/spinner";
import { showErrorMessage, showSuccessMessage } from "../../../utils/toast";
import "./otp-verification-form.scss";

interface IProps {
  value: string;
  whereTo: string;
}

const OtpVerificationForm = (props: IProps) => {
  const navigate = useNavigate();
  const { value, whereTo } = props;
  const [otp, setOtp] = useState("");
  const [isValid, setIsValid] = useState(true);
  const [isExpired, setIsExpired] = useState(false);
  const [enteredOtp, setEnteredOtp] = useState<boolean>(true);

  const handleChange = (value: string) => {
    setOtp(value);
    setEnteredOtp(value.length === 6 ? false : true);
  };

  const [triggerGetOTP, { isLoading: loadingGetOTP }] =
    useLazyValidateEmailOTPQuery();
  const [getProfile, { isFetching: isGetProfileFetching }] =
    useLazyProfileQuery();

  const resetError = () => {
    setIsValid(true);
  };

  const onSubmit = () => {
    triggerGetOTP({ email: value, otp: otp })
      .unwrap()
      .then(() => {
        if (whereTo === "reset") {
          navigate(AppRoute.RESET_PASSWORD, {
            state: {
              isNavigateToProfile: false,
            },
          });
        } else if (whereTo === "dashboard/user-profile") {
          getProfile()
            .unwrap()
            .then(() => {
              navigate(AppRoute.PROFILE_DETAILS);
              showSuccessMessage("Mobile Number updated successfully");
            });
        } else if (whereTo === "dashboard") {
          getProfile()
            .unwrap()
            .then(() => {
              navigate(AppRoute.DASHBOARD);
            });
        }
      })
      .catch((error) => {
        if (error.status === 412) {
          if (
            error.ovstErrorCode === OvstErrorCode.OVST_SEC_0005 ||
            error.ovstErrorCode === OvstErrorCode.OVST_CONS_0020
          ) {
            setIsExpired(true);
            setIsValid(true);
          } else if (
            error.ovstErrorCode === OvstErrorCode.OVST_0001 ||
            error.ovstErrorCode === OvstErrorCode.OVST_APP_SEC_0004
          ) {
            setIsExpired(false);
            setIsValid(false);
          }
        } else {
          showErrorMessage(error.message || "Something went wrong");
        }
      });
  };

  useEffect(() => {
    if (otp.length < 1) {
      setIsValid(true);
    }
  }, [otp]);

  return (
    <>
      <Row className="justify-content-center">
        <Col className="mt-5">
          <OtpInput
            value={otp}
            onChange={handleChange}
            numInputs={6}
            isInputNum={true}
            placeholder={"------"}
            inputStyle={{
              width: "52px",
              height: "70px",
              margin: "0 10px",
              fontSize: "36px",
              borderWidth: "1px",
              borderStyle: "solid",
              borderColor: "var(--input-border)",
              borderRadius: "8px",
              outline: "none",
              boxShadow: "none",
              color: "var(--body-color)",
              backgroundColor: "var(--input-bg)",
              transition:
                "border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease",
            }}
            className="input-field"
            focusStyle={{
              borderColor: "var(--primary)",
              outline: "none",
              boxShadow: "0 0 0 2px rgba(var(--primary-rgb), 0.15)",
            }}
            errorStyle={{
              borderColor: "var(--danger)",
              outline: "none",
            }}
            hasErrored={!isValid}
            containerStyle={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          />
        </Col>
      </Row>
      <Row>
        {!isValid && (
          <Col className="text-danger justify-content-center font-size-14 mt-2 text-center">
            The code you entered is incorrect. Please try again or request a new
            code.
          </Col>
        )}
        {isExpired && (
          <Col className="text-danger justify-content-center font-size-14 mt-2 text-center">
            The code you entered is expired. Please request a new code.
          </Col>
        )}
      </Row>
      <Row className="justify-content-center">
        <Col md={10}>
          <AppButton
            text="Verify"
            className={`mt-5`}
            variant="blue"
            isLoading={loadingGetOTP}
            onClick={onSubmit}
            disabled={enteredOtp}
          />
        </Col>
      </Row>
      <Row className="justify-content-center mt-4">
        <OtpVerificationFooter
          whereTo={whereTo}
          value={value}
          handleOTP={handleChange}
          resetError={resetError}
          setIsExpired={() => setIsExpired(false)}
          isEmail={true}
        />
      </Row>
      <SpinnerModal show={isGetProfileFetching} />
    </>
  );
};

export default OtpVerificationForm;
